import ClaimButton from "@components/inputs/ClaimButton";
import FlyingCoins from "@components/utils/FlyingCoins";
import { useImprovedPromotionWordings } from "@configs/statsig";
import { BALANCE_BADGE_TOP_ID } from "@constants";
import { useDailyRewardClaim } from "@hooks/useDailyRewardClaim";
import preferenceSlice from "@stores/preference/slices";
import { getThemeAsset } from "@utils/theme";
import "@views/DailyRewardPage/DailyRewardPage.css";
import { RollingNumber } from "@views/DailyRewardPage/components";
import { toRollingFormat } from "@views/DailyRewardPage/utils";
import { PageTitle, cn } from "kz-ui-sdk";
import { useEffect, useRef } from "react";
import AspectRatio from "react-aspect-ratio";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

interface DailyRewardModalContentProps {
  onClaimComplete?: () => void;
  onClaimError?: () => void;
  showComeBackTomorrow?: boolean;
  isContentFadingOut?: boolean;
}

const FRAME_CLASSES = {
  bgWheelPreClaim: "daily-reward-page__bg-wheel--pre-claimable",
  bgWheel: "daily-reward-page__bg-wheel--claimable",
  bgBtnPreClaim: "daily-reward-page__btn--pre-claimable",
  bgBtn: "daily-reward-page__btn--claimable",
  amountContainer: "daily-reward-page__amount-container",
  amountContainerPreClaim: "daily-reward-page__amount-container--pre-claimable",
};

const CLAIM_COMPLETE_DELAY_MS = 4000; // Same delay as DailyRewardPage navigation

export function DailyRewardModalContent({
  onClaimComplete,
  onClaimError,
  showComeBackTomorrow = false,
  isContentFadingOut = false,
}: DailyRewardModalContentProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getPromotionWording } = useImprovedPromotionWordings();
  const claimCompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    claimedPoint,
    isBtnClaimable,
    isButtonVisible,
    isWheelAnimated,
    isSlotAnimated,
    refFlyingCoins,
    refClaimButton,
    handleClaim: handleClaimFromHook,
    flyingCoinConfig,
    isClaiming,
  } = useDailyRewardClaim({
    autoClaimOnMount: true,
    onClaimClick: () => {
      // Unlock balance updates when "Claim Now" is clicked
      // This allows balance to update after animations complete
      dispatch(preferenceSlice.actions.setBalanceWsLocked(false));
      dispatch(preferenceSlice.actions.setHasPendingBalanceUpdate(false));

      // Clear existing timeout if any
      if (claimCompleteTimeoutRef.current) {
        clearTimeout(claimCompleteTimeoutRef.current);
        claimCompleteTimeoutRef.current = null;
      }

      // Delay calling onClaimComplete to match DailyRewardPage timing
      // This allows animations to play out before showing "come back tomorrow"
      claimCompleteTimeoutRef.current = setTimeout(() => {
        onClaimComplete?.();
        claimCompleteTimeoutRef.current = null;
      }, CLAIM_COMPLETE_DELAY_MS);
    },
    onClaimError: () => {
      // Handle claim error - refetch promotion status and close modal
      onClaimError?.();
    },
  });

  // Wrap handleClaim to ensure balance unlock happens
  const handleClaim = () => {
    handleClaimFromHook();
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (claimCompleteTimeoutRef.current) {
        clearTimeout(claimCompleteTimeoutRef.current);
        claimCompleteTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <div className="relative h-[414px] w-full overflow-hidden">
      {!showComeBackTomorrow && (
        <>
          <PageTitle
            label={t(getPromotionWording("Daily Login"))}
            variant="gradient"
            classes={{
              "label&": "w-full text-center line-clamp-2 !relative !text-xl",
              "labelOverlay&": "w-full text-center line-clamp-2 !text-xl",
              "labelContainer&": "h-auto w-[calc(100%-16px)] mx-auto text-center",
            }}
            className={cn("absolute left-1/2 top-4 -translate-x-1/2 transition-opacity duration-300 ease-in-out", {
              "opacity-0": isContentFadingOut,
              "opacity-100": !isContentFadingOut,
            })}
          />
          <div
            className={cn(
              "absolute left-1/2 top-[68px] -translate-x-1/2 text-content-soft transition-opacity duration-300 ease-in-out",
              {
                "opacity-0": isContentFadingOut,
                "opacity-100": !isContentFadingOut,
              },
            )}
          >
            {t("bonus unlocked")}
          </div>
        </>
      )}
      {/* BG Wheel */}
      <AspectRatio
        ratio={1}
        className={cn(
          "absolute left-1/2 top-0 z-[8] w-[110%] max-w-md -translate-x-1/2 bg-fixed bg-no-repeat will-change-transform",
          isWheelAnimated ? FRAME_CLASSES.bgWheel : FRAME_CLASSES.bgWheelPreClaim,
        )}
      >
        <img
          src="/images/daily/bg-wheel.png"
          className={cn("h-full w-full origin-center")}
          alt=""
        />
      </AspectRatio>
      <div
        className={cn(
          "daily-reward-page__container--pre-claimable absolute top-[-55px] z-[8] flex h-[414px] w-full items-center justify-center transition-opacity duration-300 ease-in-out",
          {
            "opacity-0": isContentFadingOut && !showComeBackTomorrow,
            "opacity-100": !isContentFadingOut || showComeBackTomorrow,
          },
        )}
      >
        {showComeBackTomorrow ? (
          <div className="flex h-[414px] w-full flex-col items-center justify-center text-center">
            {t("come back tomorrow for more")
              .split("\n")
              .map((line, index) => (
                <p
                  key={index}
                  className={cn("text-[22px] font-bold", {
                    "text-content-base": index === 0,
                    "text-content-soft": index === 1,
                  })}
                >
                  {line}
                </p>
              ))}
          </div>
        ) : (
          <>
            {/* Chest image */}
            <div
              className={cn(
                "absolute top-[112px] z-10 h-[222px] w-[240px] bg-cover bg-no-repeat opacity-100 transition [filter:drop-shadow(0px_10px_16px_black)]",
              )}
              style={{
                backgroundImage: `url(${getThemeAsset("daily-login/chest.png")})`,
              }}
            ></div>
            {/* rolling number */}
            <div
              className={cn(
                "!transition-transform-none absolute top-[225px] z-50 w-full origin-center !scale-75 will-change-transform",
                isSlotAnimated ? FRAME_CLASSES.amountContainer : FRAME_CLASSES.amountContainerPreClaim,
              )}
            >
              <RollingNumber
                claimedPoint={!!claimedPoint ? toRollingFormat(Number(claimedPoint)) : ""}
                hideShadow
              />
            </div>
            <div className="absolute left-1/2 top-[140px] z-50 h-[100px] w-[207px] -translate-x-1/2">
              <img
                src="/images/daily/chest-top-shadow.png"
                className="h-full w-full"
                alt=""
              />
            </div>

            <div
              className={cn("absolute left-1/2 top-[140px] z-20 h-[100px] w-[207px] -translate-x-1/2 bg-black/20", {
                "animate-pulse": isSlotAnimated,
              })}
            ></div>

            <FlyingCoins
              coinImgSrc={"/images/icon-coin.png"}
              sourceCoinSize={28}
              randomSizeRange={14}
              targetCoinSize={16}
              count={flyingCoinConfig.flyingCoins}
              targetId={BALANCE_BADGE_TOP_ID}
              className={cn("absolute left-1/2 top-[234px] z-10 h-[40px] w-[200px] -translate-x-1/2")}
              ref={refFlyingCoins}
              randomHorizontalRange={60}
              randomVerticalRange={70}
              delayIn={0.05}
              overlay={true}
              duration={1.5}
              removeIn={4.4}
            />

            {/*Bottom shadow*/}

            <div
              className={cn(
                "absolute top-[348px] z-50 w-full px-5 will-change-transform",
                isButtonVisible ? FRAME_CLASSES.bgBtn : FRAME_CLASSES.bgBtnPreClaim,
              )}
            >
              <ClaimButton
                size="lg"
                classes={{
                  text: "text-base",
                }}
                onClick={handleClaim}
                withCoin={isBtnClaimable && !!claimedPoint}
                withShine={isBtnClaimable && !!claimedPoint}
                claimable={isBtnClaimable && !!claimedPoint}
                text={`${t("Claim Now")}!`}
                loading={isClaiming}
                ref={refClaimButton}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
